
import { useCallback, useState, useEffect } from 'react';
import GraphConnectionService from '../services/connection/GraphConnectionService';
import { supabase } from '../integrations/supabase/client';
import SupabaseConnectionService from '../services/SupabaseConnectionService';
import LoggingService from '../services/LoggingService';

export interface GraphTeam {
  id: string;
  displayName: string;
  description?: string;
  visibility?: string;
  isArchived?: boolean;
  webUrl?: string;
  members?: number;
  compliance?: number;
  lastActivity?: string;
  files?: number;
}

export function useGraphTeams() {
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<Error | null>(null);
  const [teams, setTeams] = useState<GraphTeam[]>([]);
  const [isConfigured, setIsConfigured] = useState<boolean>(false);

  // Load Graph API configuration on mount
  useEffect(() => {
    const loadGraphConfig = async () => {
      try {
        // Get Graph API settings from Supabase
        const graphSettings = await SupabaseConnectionService.getGraphSettings();
        
        if (graphSettings) {
          // Configure GraphConnectionService with these settings
          GraphConnectionService.configure({
            clientId: graphSettings.client_id,
            tenantId: graphSettings.tenant_id,
            clientSecret: graphSettings.client_secret,
            certificate: graphSettings.certificate,
            certificatePassword: graphSettings.certificate_password,
            scopes: graphSettings.scopes,
            endpoint: graphSettings.endpoint,
          });
          
          setIsConfigured(GraphConnectionService.isProperlyConfigured());
          LoggingService.debug('Graph API configuration loaded', { isConfigured: GraphConnectionService.isProperlyConfigured() });
        } else {
          // If no settings found, make sure we reset the configured state
          setIsConfigured(false);
          LoggingService.warn('No Graph API settings found');
        }
      } catch (err) {
        console.error('Error loading Graph API configuration:', err);
        LoggingService.error('Error loading Graph API configuration', err);
        setError(err instanceof Error ? err : new Error(String(err)));
        setIsConfigured(false);
      }
    };
    
    loadGraphConfig();
  }, []);

  const fetchTeams = useCallback(async () => {
    setLoading(true);
    setError(null);
    LoggingService.debug('Fetching teams started');
    
    try {
      // Check if Graph API is properly configured
      if (!GraphConnectionService.isProperlyConfigured()) {
        const msg = 'Graph API not configured. Please configure it in settings.';
        LoggingService.warn(msg);
        throw new Error(msg);
      }
      
      // First check if we have a valid connection
      const connectionResult = await GraphConnectionService.testConnection();
      if (!connectionResult.success) {
        const msg = `Graph API connection error: ${connectionResult.error}`;
        LoggingService.error(msg);
        throw new Error(msg);
      }
      
      // Call the edge function to fetch teams
      LoggingService.debug('Calling edge function to fetch teams');
      
      // Add detailed logging for the edge function call
      LoggingService.info('Edge function parameters', {
        function: 'connect-ms-graph',
        body: {
          type: 'graph',
          operation: 'getTeams',
          config: {
            clientId: GraphConnectionService['config'].clientId,
            tenantId: GraphConnectionService['config'].tenantId,
            // Don't log sensitive info like client secret
          }
        }
      });
      
      try {
        const { data, error } = await supabase.functions.invoke('connect-ms-graph', {
          body: {
            type: 'graph',
            operation: 'getTeams',
            config: {
              clientId: GraphConnectionService['config'].clientId,
              tenantId: GraphConnectionService['config'].tenantId,
              clientSecret: GraphConnectionService['config'].clientSecret,
            }
          }
        });
        
        if (error) {
          LoggingService.error('Edge function error response', { error });
          throw new Error(`Edge function error: ${error.message}`);
        }
        
        if (!data?.success) {
          LoggingService.error('Edge function unsuccessful response', data);
          throw new Error(data?.error || 'Unknown error fetching teams');
        }
        
        // Process the teams data
        const teamsData = data.teams.map((team: any) => ({
          id: team.id,
          displayName: team.displayName,
          description: team.description || '',
          visibility: team.visibility || 'Private',
          isArchived: team.isArchived || false,
          webUrl: team.webUrl || '#',
          // Add some random data for fields not provided by Graph API
          members: Math.floor(Math.random() * 30) + 5,
          compliance: Math.floor(Math.random() * 30) + 65, // 65-95% compliance
          lastActivity: ['1 hour ago', '2 hours ago', '1 day ago', '3 days ago'][Math.floor(Math.random() * 4)],
          files: Math.floor(Math.random() * 200) + 50
        }));
        
        LoggingService.info(`Successfully fetched ${teamsData.length} teams`, { 
          teamsCount: teamsData.length,
          teamIds: teamsData.map((t: GraphTeam) => t.id)
        });
        
        setTeams(teamsData);
        return teamsData;
      } catch (edgeFunctionError) {
        // This block specifically handles errors from the edge function invocation
        LoggingService.error('Edge function invocation error', edgeFunctionError);
        throw edgeFunctionError;
      }
    } catch (err) {
      const error = err instanceof Error ? err : new Error(String(err));
      LoggingService.error('Error in fetchTeams', error);
      setError(error);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  // Add a new debug function to call the debug endpoint
  const debugGraphConnection = useCallback(async () => {
    setLoading(true);
    setError(null);
    LoggingService.debug('Running Graph API debug operation');
    
    try {
      LoggingService.info('Debug Edge function parameters', {
        function: 'connect-ms-graph',
        body: {
          type: 'graph',
          operation: 'debugGraph',
          config: {
            clientId: GraphConnectionService['config'].clientId,
            tenantId: GraphConnectionService['config'].tenantId,
            // Don't log sensitive info like client secret
          }
        }
      });
      
      // Check Supabase authentication status first
      const { data: authData, error: authError } = await supabase.auth.getSession();
      if (authError) {
        LoggingService.error('Supabase auth error before debug call', authError);
      } else {
        LoggingService.info('Auth status before debug call', { 
          isAuthenticated: Boolean(authData?.session),
          userId: authData?.session?.user?.id
        });
      }
      
      try {
        const { data, error } = await supabase.functions.invoke('connect-ms-graph', {
          body: {
            type: 'graph',
            operation: 'debugGraph',
            config: {
              clientId: GraphConnectionService['config'].clientId,
              tenantId: GraphConnectionService['config'].tenantId,
              clientSecret: GraphConnectionService['config'].clientSecret,
            }
          }
        });
        
        if (error) {
          LoggingService.error(`Error in debug operation: ${error.message}`, error);
          throw new Error(`Debug operation failed: ${error.message}`);
        }
        
        LoggingService.info('Debug operation results', data);
        return data;
      } catch (edgeFunctionError) {
        // This block specifically handles errors from the edge function invocation
        LoggingService.error('Edge function debug invocation error', edgeFunctionError);
        throw edgeFunctionError;
      }
    } catch (err) {
      const error = err instanceof Error ? err : new Error(String(err));
      LoggingService.error('Error in debugGraphConnection', error);
      setError(error);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  return {
    teams,
    loading,
    error,
    fetchTeams,
    debugGraphConnection,
    isConfigured
  };
}
